/**
 * useClusterRowSelection — bridges the item list's synthetic cluster entries to
 * item-id selection state.
 *
 * A settled cluster shows as one entry whose row id is `cluster:<groupId>`, not
 * an item id (see collapseSettledClusters). Selection state must never hold that
 * synthetic id — bulk edit, export and delete all resolve ids against the items
 * store. So this hook translates at the table boundary:
 *
 * - the table sees a cluster entry as checked when all of its members are
 * - checking a cluster entry selects (or clears) every member
 * - "select all" expands cluster entries into their members
 */

import { useCallback, useMemo } from 'react';
import type { ItemRow } from './useItemListData';

export interface ClusterRowSelection {
  /** Selection set to hand the table (real item ids + satisfied cluster entries). */
  tableSelectedRows: Set<string>;
  /** Row-checkbox handler that understands cluster entries. */
  handleToggleRow: (rowId: string) => void;
  /** Select-all handler that expands cluster entries into member ids. */
  handleSelectAllRows: (rowIds: string[]) => void;
  /** Group-checkbox handler: selects or clears a set of rows, cluster entries expanded. */
  handleSetRowsSelected: (rowIds: string[], selected: boolean) => void;
}

export function useClusterRowSelection({
  displayRows,
  selectedRows,
  toggleRowSelection,
  setRowsSelected,
  selectAllRows,
}: {
  displayRows: ItemRow[];
  selectedRows: Set<string>;
  toggleRowSelection: (id: string) => void;
  setRowsSelected: (ids: string[], selected: boolean) => void;
  selectAllRows: (ids: string[]) => void;
}): ClusterRowSelection {
  const clusterMembersByRowId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of displayRows) {
      if (row.clusterEntry) map.set(row.id, row.clusterEntry.memberIds);
    }
    return map;
  }, [displayRows]);

  const tableSelectedRows = useMemo(() => {
    if (clusterMembersByRowId.size === 0) return selectedRows;
    const next = new Set(selectedRows);
    for (const [rowId, memberIds] of clusterMembersByRowId) {
      if (memberIds.every((id) => selectedRows.has(id))) next.add(rowId);
    }
    return next;
  }, [selectedRows, clusterMembersByRowId]);

  const handleToggleRow = useCallback(
    (rowId: string) => {
      const memberIds = clusterMembersByRowId.get(rowId);
      if (!memberIds) {
        toggleRowSelection(rowId);
        return;
      }
      setRowsSelected(memberIds, !memberIds.every((id) => selectedRows.has(id)));
    },
    [clusterMembersByRowId, toggleRowSelection, setRowsSelected, selectedRows],
  );

  const handleSelectAllRows = useCallback(
    (rowIds: string[]) =>
      selectAllRows(rowIds.flatMap((id) => clusterMembersByRowId.get(id) ?? [id])),
    [selectAllRows, clusterMembersByRowId],
  );

  const handleSetRowsSelected = useCallback(
    (rowIds: string[], selected: boolean) =>
      setRowsSelected(rowIds.flatMap((id) => clusterMembersByRowId.get(id) ?? [id]), selected),
    [setRowsSelected, clusterMembersByRowId],
  );

  return { tableSelectedRows, handleToggleRow, handleSelectAllRows, handleSetRowsSelected };
}
